// src/components/layout/PasscodeGate.tsx

import React, { useState, useEffect } from "react";
import MRNLogo from "@/components/brand/MRNLogo";
import { Lock, Eye, EyeOff } from "lucide-react";
import { clsx } from "clsx";

// ── Change this to whatever passcode you want ─────────────────
const ACCESS_CODE = "MRN2026";
const SESSION_KEY = "mrn_access";

interface PasscodeGateProps {
  children: React.ReactNode;
}

export default function PasscodeGate({ children }: PasscodeGateProps) {
  const [unlocked, setUnlocked]   = useState(false);
  const [code, setCode]           = useState("");
  const [error, setError]         = useState(false);
  const [showCode, setShowCode]   = useState(false);
  const [shaking, setShaking]     = useState(false);

  // Check session on mount
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === "true") {
      setUnlocked(true);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().toUpperCase() === ACCESS_CODE.toUpperCase()) {
      sessionStorage.setItem(SESSION_KEY, "true");
      setUnlocked(true);
    } else {
      setError(true);
      setShaking(true);
      setCode("");
      setTimeout(() => { setShaking(false); setError(false); }, 1500);
    }
  };

  if (unlocked) return <>{children}</>;

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-blue/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-cyan/5 blur-3xl" />
      </div>

      <div className={clsx(
        "relative w-full max-w-sm",
        shaking && "animate-[shake_0.4s_ease-in-out]"
      )}>
        {/* Card */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 text-center">

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <MRNLogo variant="full" size={56} onDark={true} />
          </div>

          {/* Tagline */}
          <p className="text-white/50 text-xs mb-8 tracking-wide">
            Retail Intelligence &amp; Activation Platform
          </p>

          {/* Lock icon */}
          <div className="w-12 h-12 rounded-full bg-white/8 border border-white/10 flex items-center justify-center mx-auto mb-6">
            <Lock size={18} className="text-white/60" />
          </div>

          <h2 className="text-white text-lg font-semibold mb-1">Access Required</h2>
          <p className="text-white/40 text-xs mb-6">Enter your access code to continue</p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <input
                type={showCode ? "text" : "password"}
                value={code}
                onChange={e => { setCode(e.target.value); setError(false); }}
                placeholder="Access code"
                autoFocus
                className={clsx(
                  "w-full bg-transparent border rounded-lg px-4 py-3 text-white text-sm text-center tracking-widest",
                  "placeholder:text-white/25 placeholder:tracking-normal",
                  "focus:outline-none focus:ring-2 transition-all duration-150",
                  error
                    ? "border-red-400/60 focus:ring-red-400/30"
                    : "border-white/10 focus:ring-blue/40 focus:border-blue/40"
                )}
              />
              <button
                type="button"
                onClick={() => setShowCode(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
              >
                {showCode ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>

            {error && (
              <p className="text-red-400 text-xs animate-fade-in">
                Incorrect access code. Please try again.
              </p>
            )}

            <button
              type="submit"
              disabled={!code.trim()}
              className={clsx(
                "w-full py-3 rounded-lg text-sm font-semibold transition-all duration-150",
                "bg-blue text-white hover:bg-navy disabled:opacity-40 disabled:cursor-not-allowed",
                "focus:outline-none focus:ring-2 focus:ring-blue/40"
              )}
            >
              Enter Platform
            </button>
          </form>

          <p className="text-white/20 text-2xs mt-6">
            MRN — McClatchy Retail Network · Confidential
          </p>
        </div>
      </div>

      {/* Shake keyframe */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-8px); }
          40%       { transform: translateX(8px); }
          60%       { transform: translateX(-6px); }
          80%       { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}